/**
 * Premium plant batch definitions.
 *
 * Three distinct silhouettes (ribbon, leafy, coral) share one palette family
 * and one sway model so they read as a set.
 */

export type PremiumPlantId = 'ribbon' | 'leafy' | 'coral';

export interface PremiumPlantPalette {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  accent: string;
  stem: string;
  glow: string;
}

export interface PremiumPlantMotion {
  sway: number;
  float: number;
  delay: number;
  amp: string;
}

export interface PremiumPlantDef {
  id: PremiumPlantId;
  label: string;
  palette: PremiumPlantPalette;
  motion: PremiumPlantMotion;
}

export const PREMIUM_PLANTS: Record<PremiumPlantId, PremiumPlantDef> = {
  ribbon: {
    id: 'ribbon',
    label: 'Ribbon',
    palette: {
      primary: '#3fae86',
      primaryLight: '#6ed3a6',
      primaryDark: '#2b7f63',
      accent: '#9de8c0',
      stem: '#2f8f6c',
      glow: '#d6f7e6',
    },
    motion: { sway: 5.6, float: 6.4, delay: 0, amp: '5deg' },
  },
  leafy: {
    id: 'leafy',
    label: 'Leafy',
    palette: {
      primary: '#5ecf8f',
      primaryLight: '#8fe3b4',
      primaryDark: '#3aa46e',
      accent: '#c8f0d8',
      stem: '#2f8f6c',
      glow: '#e2fbec',
    },
    motion: { sway: 6.8, float: 7.2, delay: 0.4, amp: '6deg' },
  },
  coral: {
    id: 'coral',
    label: 'Soft Coral',
    palette: {
      primary: '#ff9ec0',
      primaryLight: '#ffc2d6',
      primaryDark: '#e05a86',
      accent: '#ffe3ee',
      stem: '#c86a94',
      glow: '#ffd6e6',
    },
    motion: { sway: 4.2, float: 5.2, delay: 0.2, amp: '4deg' },
  },
};

export const PREMIUM_PLANT_ORDER: PremiumPlantId[] = ['ribbon', 'leafy', 'coral'];

export const RIBBON_BLADES = [
  'M 70 210 C 63 160 58 110 64 62 C 67 38 70 20 71 6 C 75 22 78 42 79 64 C 84 112 77 162 70 210 Z',
  'M 66 210 C 52 165 40 120 34 78 C 31 56 33 40 38 28 C 44 44 50 60 54 80 C 62 122 70 165 72 210 Z',
  'M 62 210 C 44 170 28 132 20 96 C 16 76 18 60 24 50 C 30 66 38 82 44 102 C 54 140 66 174 70 210 Z',
  'M 74 210 C 88 165 100 120 106 78 C 109 56 107 40 102 28 C 96 44 90 60 86 80 C 78 122 70 165 68 210 Z',
  'M 78 210 C 96 170 112 132 120 96 C 124 76 122 60 116 50 C 110 66 102 82 96 102 C 86 140 74 174 70 210 Z',
];

export const LEAFY_LEAVES = [
  { d: 'M 74 150 C 46 148 22 132 18 112 C 36 104 60 118 74 136 Z', side: 'left' },
  { d: 'M 76 120 C 104 118 128 102 132 82 C 114 74 90 88 76 106 Z', side: 'right' },
  { d: 'M 74 90 C 46 88 22 72 18 52 C 36 44 60 58 74 76 Z', side: 'left' },
  { d: 'M 76 60 C 104 58 128 42 132 22 C 114 14 90 28 76 46 Z', side: 'right' },
  { d: 'M 75 42 C 64 22 66 6 75 0 C 84 6 86 22 75 42 Z', side: 'center' },
];
